import React, { ChangeEvent, FC, useState, useEffect } from 'react';
import { useParams, useRouter } from 'next/navigation';
import Button from 'components/commercetools-ui/atoms/button';
import Input from 'components/commercetools-ui/atoms/input';
import PasswordInput from 'components/commercetools-ui/atoms/input-password';
import Typography from 'components/commercetools-ui/atoms/typography';
import { useFormat } from 'helpers/hooks/useFormat';
import useTranslation from 'providers/i18n/hooks/useTranslation';
import { useAccount } from 'frontastic';
import useAuthProps from './hooks/useAuthProps';
import ResetPassword from './reset-password';
import { EMAIL_PATTERN } from '../company-registration/data';
import AuthForm from '../layouts/auth-form';

type ChangeHandler = (event: ChangeEvent<HTMLInputElement>) => void;

const LoginPopup: FC<any> = ({
  setBootLogin,
  setShowPopup,
  success,
  setAccountCreated,
  failed,
  handleModalCancel,
  defaultLoginModal = false,
  error_description = '',
}) => {
  const { formatMessage } = useFormat({ name: 'account' });
  const { auth0Login, resendEmailVerification, account, loggedIn } = useAccount();
  const { locale } = useParams();
  const router = useRouter();
  const { translate } = useTranslation();

  const [error, setError] = useState<string>();
  const [resetting, setResetting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>({});
  const [showReset, setShowResetForm] = useState(false);
  const [showCompleteProfile, setShowCompleteProfile] = useState(false);
  const [emailReminders, setEmailRemainders] = useState<number | undefined>(undefined);
  const [isInitializing, setIsInitializing] = useState(true);

  const inputArray = [{ label: translate('common.emailAddress'), name: 'email' }];

  const handleBlur = () => {
    const emailRegex = EMAIL_PATTERN;
    if (!data.email || !emailRegex.test(data.email)) {
      setError(translate('error.auth.email.wrong'));
    } else {
      setError('');
    }
  };
  const handlePwdBlur = () => {
    const capitalRegex = /[A-Z]/;
    const specialCharRegex = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;
    const numberRegex = /[0-9]/;

    if (
      !data.password ||
      data.password.length < 8 ||
      !capitalRegex.test(data.password) ||
      !specialCharRegex.test(data.password) ||
      !numberRegex.test(data.password)
    ) {
      setError(translate('error.auth.password.wrong'));
    } else {
      setError('');
    }
  };

  const onResendVerfication = async () => {
    try {
      const response: any = await resendEmailVerification({ email: data.email });
      if (response?.data?.StatusCode === 'OK') {
        setEmailRemainders(undefined);
      } else {
        setEmailRemainders(undefined);
      }
    } catch (e: any) {
      const errorMessage = e.message?.replace(/^Error:\s*/, '');
      setError(errorMessage);
      setEmailRemainders(undefined);
    }
  };

  const handleSubmit = async () => {
    setEmailRemainders(undefined);
    if (!data.email || !data.password) {
      setError(translate('error.auth.wrong'));
      return;
    }
    const emailRegex = EMAIL_PATTERN;
    if (!emailRegex.test(data.email)) {
      setError(translate('error.auth.email.wrong'));
      return;
    }
    if (data.email && data.password) {
      const encodedPassword = Buffer.from(data.password).toString('base64');
      const email = data.email.toLowerCase();
      const requestBody = {
        Username: email,
        Password: encodedPassword,
        storeKey: `specify-${locale}`,
      };
      setLoading(true);
      try {
        const responseBody: any = await auth0Login(requestBody);
        if (responseBody?.loginStatusCode === 'OK') {
          if (!responseBody.firstName || !responseBody.companyName) {
            setShowCompleteProfile(true);
          } else {
            setLoading(false);
            handleOnClosePopup();
          }
        } else {
          setLoading(false);
          if ('emailRemainderCount' in responseBody) {
            setEmailRemainders(responseBody?.emailRemainderCount);
          } else {
            setError('Error logging in user. Please check your email/password.');
          }
        }
      } catch (error: any) {
        const errorMessage = error.message?.replace(/^Error:\s*/, '');
        if (errorMessage.toLowerCase() === 'failed to fetch') {
          setError(translate('error.no.internet'));
        } else {
          setError(errorMessage);
          if (errorMessage === 'Wrong email or password.') {
            setError(translate('error.wrong.email'));
          }
        }
        setLoading(false);
      }
    }
  };

  const handleResetSubmit = async () => {
    if (data.email) {
      const email = data.email.toLowerCase();
      const requestBody = {
        email: email,
      };
      setLoading(true);
      try {
        // const responseBody = await extensions.account.handleResetSubmit(JSON.stringify(requestBody));
        const responseBody: any = {};
        if (responseBody?.data?.data?.StatusCode === 200) {
          setLoading(false);
          router.push(`/password-reset-confirm?email=${email}`);
        } else {
          setLoading(false);
          setError(translate('error.email'));
        }
      } catch (error) {
        setLoading(false);
        console.error('Reset Pasword Error:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleOnClosePopup = () => {
    setShowPopup(false, () => {
      setError('');
      setData({});
      setAccountCreated(false);
    });
    if (!defaultLoginModal) {
      setBootLogin(false);
    }
  };

  const goBackToLogin = () => {
    setResetting(false);
  };

  const authProps = useAuthProps({
    handleLoginSubmit: handleSubmit,
    handleResetSubmit,
    resetting,
    goBackToLogin,
    handleOnClosePopup,
  });

  const callbackData = () => {
    setResetting(false);
    setError('');
    setData({});
  };

  const handleChange = (name: string, value: string) => {
    if (name === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!value || !emailRegex.test(value)) {
        setError(translate('error.auth.email.wrong'));
      } else {
        setError('');
      }
    }
    if (name === 'password' || name === 'confirmPassword') {
      const capitalRegex = /[A-Z]/;
      const specialCharRegex = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;
      const numberRegex = /[0-9]/;

      if (
        !value ||
        value.length < 8 ||
        !capitalRegex.test(value) ||
        !specialCharRegex.test(value) ||
        !numberRegex.test(value)
      ) {
        setError(translate('error.auth.password.wrong'));
      } else {
        setError('');
      }
    }
    setData((prevData: any) => ({ ...prevData, [name]: value }));
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pastedData = event.clipboardData.getData('text');
    handleChange('email', pastedData);
  };
  const handlePwdPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pastedData = event.clipboardData.getData('text');
    handleChange('password', pastedData);
  };
  const handleInputChange: ChangeHandler = (event) => {
    const { name, value } = event.target;
    handleChange(name, value);
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSubmit();
    }
  };
  useEffect(() => {
    if (failed) {
      setError(failed);
    }
  }, [failed]);

  useEffect(() => {
    if (loggedIn === undefined) return;

    if (loggedIn) {
      if (account !== undefined) {
        setIsInitializing(false);
        if (!account?.companyName) {
          setShowCompleteProfile(true);
        }
      }
    } else {
      setIsInitializing(false);
    }
  }, [loggedIn, account]);

  const completeProfile = () => {
    router.push('/company-registration');
  };

  if (isInitializing) return null;

  return showReset ? (
    <ResetPassword
      {...authProps}
      setShowResetPopup={setShowResetForm}
      setShowEntirePopup={setShowPopup}
      handleModalCancel={handleModalCancel}
      setBootLogin={setBootLogin}
    />
  ) : showCompleteProfile ? (
    <div className="flex !flex-col items-center justify-center gap-32 !p-16 sm:flex-row sm:gap-32 ">
      <Typography className="text-18 font-medium text-black">{`Complete your profile to continue.`}</Typography>
      <Button
        onClick={() => completeProfile()}
        className={
          'border-1 z-10 w-full cursor-pointer rounded-none border !border-[#2B2C39] !bg-[#2B2C39] px-25 py-13 shadow-sm hover:!bg-[#394A54] lg:w-fit'
        }
      >
        <Typography as="span" className={`text-14 font-medium text-[#fff]`}>
          Complete Profile
        </Typography>
      </Button>
    </div>
  ) : (
    <>
      <AuthForm
        {...authProps}
        error={error}
        loading={loading}
        handleModalCancel={handleModalCancel}
        success={
          success
            ? formatMessage({
                id: 'account.success',
                defaultMessage: 'Your account creation has been successful',
              })
            : ''
        }
      >
        <>
          {error_description && <p className="justify-center py-5 text-16 text-red-400">{error_description}</p>}
          {emailReminders ? (
            <p className="text-gray-600">
              Your email is not verified. Please{' '}
              <button
                onClick={onResendVerfication}
                className="cursor-pointer bg-transparent font-semibold text-gray-600 underline"
              >
                click here
              </button>
              {'  '}
              to resend the verification link.
            </p>
          ) : (
            ''
          )}
          {inputArray.map(({ label, name }) => (
            <Input
              key={name}
              name={name}
              placeholder={translate('common.emailAddress')}
              value={(data[name] as string) ?? ''}
              required
              onBlur={handleBlur}
              onChange={handleInputChange}
              onPaste={handlePaste}
              onKeyDown={handleKeyDown}
              tabIndex={0}
              className={`${error ? '!border-red-500' : '!border-[#A7A7A7]'} !rounded-[6px] `}
            />
          ))}
          <div>
            <PasswordInput
              name="password"
              value={data.password ?? ''}
              className={`${error ? '!border-red-500' : '!border-[#A7A7A7]'} !rounded-[6px] `}
              placeholder={translate('account.password')}
              onChange={handleInputChange}
              onBlur={handlePwdBlur}
              onPaste={handlePwdPaste}
              onKeyDown={handleKeyDown}
              tabIndex={0}
            />
          </div>
          <div className="flex flex-row !items-center !justify-center !gap-4">
            <Typography className="!text-center !text-14 !leading-loose !text-gray-600">
              Forgot your password ?
            </Typography>
            <span
              onClick={() => setShowResetForm(true)}
              className="!w-fit cursor-pointer text-center !text-14 !font-medium leading-loose !text-gray-600 !underline underline-offset-4 hover:!text-gray-500"
            >
              Reset Here
            </span>
          </div>
        </>
      </AuthForm>
      {!success && (
        <Button
          onClick={() => setBootLogin(false)}
          disabled={loading}
          className="border-1 flex w-full cursor-pointer items-center justify-center rounded-none !border-[#666] !bg-white px-18 py-8 shadow-sm  hover:!bg-[#F4F4F4]"
        >
          <span className="text-16 font-semibold text-[#333]">{'Create Account'}</span>
        </Button>
      )}
    </>
  );
};

export default LoginPopup;
